// src/pages/roster/PlayerModal.jsx
import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

// Sport-specific position lists. Used to filter the position datalist
// down to the team's actual sport instead of mixing all sports
// together (a soccer coach shouldn't see "Quarterback" in the dropdown).
// Sports not listed here fall back to the union of all positions so
// custom / "Other" sports still get suggestions.
//
// Within each sport list, broad/general positions come FIRST (parents
// pick these quickly), then specific variants (coaches use these for
// lineup planning). The field is a free-text input with datalist
// autocomplete, so users can also type a value not in the list.
const POSITIONS = {
  Soccer: [
    // Broad — most parents and casual users pick from here
    'Goalkeeper',
    'Defender',
    'Midfielder',
    'Forward',
    // Specific defender variants
    'Center Back',
    'Right Back',
    'Left Back',
    'Wing Back',
    'Sweeper',
    // Specific midfielder variants
    'Defensive Midfielder',
    'Central Midfielder',
    'Attacking Midfielder',
    'Right Midfielder',
    'Left Midfielder',
    // Specific attacker variants
    'Striker',
    'Center Forward',
    'Right Winger',
    'Left Winger',
  ],
  Basketball: [
    // Broad
    'Guard',
    'Forward',
    'Center',
    // Specific
    'Point Guard',
    'Shooting Guard',
    'Small Forward',
    'Power Forward',
  ],
  Baseball: [
    'Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base',
    'Shortstop', 'Outfield',
    // Specific outfield variants
    'Left Field', 'Center Field', 'Right Field',
    // Utility
    'Designated Hitter', 'Utility',
  ],
  Softball: [
    'Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base',
    'Shortstop', 'Outfield',
    'Left Field', 'Center Field', 'Right Field',
    'Designated Player', 'Utility',
  ],
  Football: [
    'Quarterback', 'Running Back', 'Wide Receiver', 'Tight End',
    'Offensive Line', 'Defensive Line', 'Linebacker', 'Cornerback',
    'Safety', 'Kicker',
    // Specific OL variants
    'Center', 'Guard', 'Tackle',
    // Specific DL variants
    'Defensive End', 'Defensive Tackle', 'Nose Tackle',
    // Other
    'Punter', 'Long Snapper', 'Return Specialist',
  ],
  Volleyball: [
    'Setter', 'Outside Hitter', 'Middle Blocker', 'Opposite', 'Libero',
    'Defensive Specialist',
  ],
  Lacrosse: [
    'Attack', 'Midfield', 'Defense', 'Goalie',
    'Long Stick Midfielder', 'Faceoff Specialist',
  ],
  Hockey: [
    'Forward', 'Defense', 'Goalie',
    // Specific forward variants
    'Center', 'Left Wing', 'Right Wing',
  ],
}

// PlayerModal collects the PRIMARY contact only. Multi-guardian
// support (mom + dad, divorced households, grandparents, etc.) lives
// in the parent-driven flows instead — the registration form when a
// parent enrolls their kid, and the parent self-service portal once
// they have an account. Reasoning:
//   - The coach is doing fast data entry. They typically have one
//     email from the team signup sheet.
//   - The parent knows their family structure and should own those
//     details (legal authorization, emergency contacts, etc.).
// We keep writing to a `guardians[]` array on the player doc so the
// data model is forward-compatible with multi-guardian — when the
// registration form catches up, parents can layer in additional
// guardians without any migration.
const RELATIONSHIPS = [
  '', // blank = "not specified"
  'Mom',
  'Dad',
  'Guardian',
  'Grandparent',
  'Step-parent',
  'Other',
]

// Player status drives the roster split (Active / Prospects /
// Inactive tabs) and gates "Invite all parents" — coaches running
// tryouts add 30+ kids as prospects without spamming any family,
// then promote the picked ones to active and bulk-invite once.
//
// Tooltip strings live here so PlayerModal and RosterPage can share
// the same vocabulary.
const STATUSES = [
  { value: 'active',   label: 'Active',   sub: 'On the roster, ready to play' },
  { value: 'prospect', label: 'Prospect', sub: 'Trying out / under evaluation' },
  { value: 'inactive', label: 'Inactive', sub: 'No longer with team' },
]

// Defensive string coercion. Existing player records may have
// jerseyNumber stored as a number (e.g. 7) instead of a string,
// so calling .trim() directly on form values would crash. We
// coerce on init AND on submit so any mixed-type field flows
// through cleanly.
const safeStr = (v) => (v == null ? '' : String(v))

const blankGuardian = () => ({
  name: '',
  email: '',
  phone: '',
  language: 'en',
  relationship: '',
})

// Reconstruct the guardians array from an existing player record.
// Migration path: new records use `guardians: [...]` directly; older
// records only have flat parentName/parentEmail/parentPhone fields,
// so we synthesize a single guardian from those. Any record gets at
// least one guardian object so the UI always has something to render.
const guardiansFromPlayer = (player) => {
  if (!player) return [blankGuardian()]
  if (Array.isArray(player.guardians) && player.guardians.length > 0) {
    return player.guardians.map((g) => ({
      name:         safeStr(g?.name),
      email:        safeStr(g?.email),
      phone:        safeStr(g?.phone),
      language:     safeStr(g?.language) || 'en',
      relationship: safeStr(g?.relationship),
    }))
  }
  // Legacy single-parent fields → first guardian slot
  if (player.parentName || player.parentEmail || player.parentPhone) {
    return [{
      name:         safeStr(player.parentName),
      email:        safeStr(player.parentEmail),
      phone:        safeStr(player.parentPhone),
      language:     safeStr(player.language) || 'en',
      relationship: '',
    }]
  }
  return [blankGuardian()]
}

export default function PlayerModal({ player, sport, roster = [], onSave, onClose }) {
  const { t } = useTranslation()
  const isEdit = !!player

  // Pick the position list for the team's sport. Falls back to all
  // positions for unrecognized sports (e.g. "Other") so the dropdown
  // is still useful instead of empty.
  const positionsForSport = POSITIONS[sport] || Object.values(POSITIONS).flat()

  // Jersey-number conflict detection. We don't HARD-block duplicates —
  // some coaches legitimately want them (practice squad, two players
  // share a kit mid-season, league doesn't enforce uniqueness). But we
  // surface conflicts inline so the coach sees them and can decide.
  // The map below is { '10': 'Alex Garcia', '7': 'Maya Park', ... }
  // and excludes the player currently being edited so re-saving them
  // doesn't flag their own number as a conflict.
  const usedNumbers = useMemo(() => {
    const map = {}
    roster.forEach((p) => {
      if (isEdit && p.id === player.id) return
      const n = safeStr(p.jerseyNumber).trim()
      if (n) map[n] = p.displayName
    })
    return map
  }, [roster, isEdit, player])

  const [form, setForm] = useState({
    firstName:    safeStr(player?.firstName),
    lastName:     safeStr(player?.lastName),
    jerseyNumber: safeStr(player?.jerseyNumber),
    position:     safeStr(player?.position),
    // Status is REQUIRED, no default. Forcing the coach to make a
    // deliberate pick prevents accidentally flagging a tryout
    // candidate as "active" — which would expose them to the bulk
    // parent-invite flow and contact families of kids who haven't
    // even been offered a roster spot yet. Worst-case false-positive
    // there is embarrassing and undoable.
    //
    // Existing player records loaded without a status field still
    // count as 'active' for tab/filter purposes (see playerStatusOf
    // in RosterPage) so legacy data isn't hidden — but the form
    // itself surfaces the empty value so the coach can confirm or
    // change it on edit.
    status:       safeStr(player?.status),
    notes:        safeStr(player?.notes),
  })
  const [guardians, setGuardians] = useState(() => guardiansFromPlayer(player))

  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  const onChange = (e) => {
    const { name, value } = e.target
    setForm((p) => ({ ...p, [name]: value }))
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }))
  }

  // PlayerModal only edits the primary guardian (index 0). Any
  // additional guardians on the record (added via the parent
  // registration / portal flows) are preserved untouched on save.
  const primaryGuardian = guardians[0] || blankGuardian()
  const updatePrimary = (patch) => {
    setGuardians((prev) => {
      const next = prev.length > 0 ? [...prev] : [blankGuardian()]
      next[0] = { ...next[0], ...patch }
      return next
    })
  }

  const validate = () => {
    const e = {}
    if (!safeStr(form.firstName).trim()) e.firstName = t('playerModal.errFirst')
    if (!safeStr(form.lastName).trim())  e.lastName  = t('playerModal.errLast')
    if (!safeStr(form.status).trim())    e.status    = t('playerModal.errStatus')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  // Single source of truth for the save payload. Both the regular
  // submit and the "Save & add another" flow funnel through here so
  // the data shape stays consistent.
  const buildSavePayload = () => {
    // Clean the guardians array: drop entries that are completely
    // empty so we don't write blank objects to Firestore. Keep at
    // least one guardian record (even if empty) so reads always get
    // a stable shape.
    const cleanedGuardians = guardians
      .map((g) => ({
        name:         safeStr(g.name).trim(),
        email:        safeStr(g.email).trim().toLowerCase(),
        phone:        safeStr(g.phone).trim(),
        language:     safeStr(g.language) || 'en',
        relationship: safeStr(g.relationship).trim(),
      }))
      .filter((g, i) => i === 0 || g.name || g.email || g.phone)

    const primary = cleanedGuardians[0] || blankGuardian()

    // Defensive String() wraps everywhere — protects against any
    // legacy field with a non-string value flowing through onChange.
    // We write BOTH the new `guardians` array AND the legacy
    // parentName/parentEmail/parentPhone/language fields populated
    // from the primary guardian for backward compat.
    return {
      firstName:    safeStr(form.firstName).trim(),
      lastName:     safeStr(form.lastName).trim(),
      jerseyNumber: safeStr(form.jerseyNumber).trim() || null,
      position:     safeStr(form.position).trim() || null,
      status:       safeStr(form.status) || 'active',
      guardians:    cleanedGuardians,
      parentName:   primary.name || null,
      parentEmail:  primary.email || null,
      parentPhone:  primary.phone || null,
      language:     primary.language || 'en',
      notes:        safeStr(form.notes).trim() || null,
    }
  }

  // Reset the form to a clean slate after "Save & add another" so the
  // coach can rapid-fire the next entry without re-clearing fields by
  // hand. Status is reset to empty deliberately — we want the
  // deliberate-pick-required behavior on every new player, not
  // "remember last status" which would be a footgun for tryout flows.
  const resetForm = () => {
    setForm({
      firstName:    '',
      lastName:     '',
      jerseyNumber: '',
      position:     '',
      status:       '',
      notes:        '',
    })
    setGuardians([blankGuardian()])
    setErrors({})
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    try {
      await onSave(buildSavePayload(), isEdit ? player.id : null)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  // "Save & add another" path: writes the player but signals the
  // parent NOT to close the modal, then resets the form for fast
  // successive entry. Only available in add mode (no point on edit).
  const handleSubmitAndAddAnother = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      await onSave(buildSavePayload(), null, { keepOpen: true })
      resetForm()
      // Scroll back to the top so the next entry starts fresh from
      // the first field (form is taller than the modal viewport).
      try {
        const el = document.getElementById('player-modal-scroll')
        if (el) el.scrollTo({ top: 0, behavior: 'smooth' })
      } catch {}
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const inputClass =
    'mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 transition-colors'
  const labelClass = 'block text-[11px] font-bold uppercase tracking-[0.12em] text-[#0d1f14] mb-0.5'
  const errorClass = 'mt-1 text-xs text-red-500'

  const sectionLabel = (
    'text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3'
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">

        {/* Pitch-dark header strip with lime accent, mirrors the
            welcome modal + bug button modal styling */}
        <div
          className="relative px-6 py-4 bg-[#0d1f14] text-white border-b border-white/10"
          style={{
            backgroundImage:
              'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#b5f03d]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {t('playerModal.eyebrow')}
              </p>
              <h2
                className="text-xl font-bold uppercase tracking-tight mt-0.5"
                style={{ fontFamily: HEAD_FONT }}
              >
                {isEdit ? t('playerModal.titleEdit') : t('playerModal.titleAdd')}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('playerModal.close')}
              className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form
          id="player-modal-scroll"
          onSubmit={handleSubmit}
          className="overflow-y-auto max-h-[70vh]"
        >
          <div className="p-6 space-y-6">

            {/* Player info */}
            <div>
              <p className={sectionLabel} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.sectionPlayer')}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.firstName')} *</label>
                  <input
                    name="firstName"
                    value={form.firstName}
                    onChange={onChange}
                    className={inputClass}
                    placeholder={t('playerModal.phFirst')}
                  />
                  {errors.firstName && <p className={errorClass}>{errors.firstName}</p>}
                </div>
                <div>
                  <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.lastName')} *</label>
                  <input
                    name="lastName"
                    value={form.lastName}
                    onChange={onChange}
                    className={inputClass}
                    placeholder={t('playerModal.phLast')}
                  />
                  {errors.lastName && <p className={errorClass}>{errors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.jersey')}</label>
                  <input
                    name="jerseyNumber"
                    value={form.jerseyNumber}
                    onChange={onChange}
                    className={inputClass}
                    placeholder={t('playerModal.phJersey')}
                  />
                  {/* Conflict warning: shows inline if the coach types
                      a number another roster spot already has. Soft —
                      doesn't block save (some coaches legitimately
                      duplicate). Helps avoid silent collisions. */}
                  {(() => {
                    const trimmed = safeStr(form.jerseyNumber).trim()
                    const conflictingPlayer = trimmed && usedNumbers[trimmed]
                    if (conflictingPlayer) {
                      return (
                        <p className="mt-1 text-xs text-amber-600">
                          {t('playerModal.conflict', { num: trimmed, name: conflictingPlayer })}
                        </p>
                      )
                    }
                    // Hint of taken numbers when no conflict — helps the
                    // coach pick a free one without leaving the modal.
                    const taken = Object.keys(usedNumbers)
                    if (taken.length > 0) {
                      const sorted = [...taken].sort((a, b) => {
                        const na = parseInt(a, 10)
                        const nb = parseInt(b, 10)
                        if (Number.isNaN(na) || Number.isNaN(nb)) return a.localeCompare(b)
                        return na - nb
                      })
                      return (
                        <p className="mt-1 text-xs text-zinc-500">
                          {t('playerModal.taken', { list: sorted.map((n) => `#${n}`).join(', ') })}
                        </p>
                      )
                    }
                    return null
                  })()}
                </div>
                <div>
                  <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.position')}</label>
                  <input
                    name="position"
                    value={form.position}
                    onChange={onChange}
                    className={inputClass}
                    placeholder={t('playerModal.phPosition')}
                    list="positions-list"
                    // Without autoComplete="off" Chrome merges its own
                    // remembered values for any field named "position"
                    // into the datalist suggestions. Coaches saw stale
                    // entries like "Keeper" appear next to our curated
                    // list, even though "Keeper" isn't anywhere in our
                    // data. Disabling browser autofill keeps the
                    // dropdown clean — datalist suggestions only.
                    autoComplete="off"
                  />
                  <datalist id="positions-list">
                    {positionsForSport.map((p) => (
                      <option key={p} value={p} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Player status — REQUIRED. Three big radio-style
                  buttons instead of a dropdown so the coach makes a
                  deliberate, visible choice instead of leaving a
                  silently-defaulted "active" that could trigger
                  premature bulk parent-invites. */}
              <div className="mt-4">
                <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>
                  {t('playerModal.status')} *
                </label>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {STATUSES.map((s) => {
                    const active = form.status === s.value
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => {
                          setForm((p) => ({ ...p, status: s.value }))
                          if (errors.status) setErrors((p) => ({ ...p, status: null }))
                        }}
                        className={[
                          'flex flex-col items-center justify-center gap-1 rounded-xl border-2 px-2 py-3 text-center transition-all',
                          active
                            ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                            : 'border-zinc-200 bg-white hover:border-[#b5f03d]/40 hover:bg-[#f7faf5]',
                          errors.status && !active ? 'border-red-300' : '',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'text-xs font-bold uppercase tracking-wide',
                            active ? 'text-[#0d1f14]' : 'text-zinc-700',
                          ].join(' ')}
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                        >
                          {t(`playerModal.statuses.${s.value}.label`)}
                        </span>
                        <span className="text-[10px] leading-tight text-zinc-500">
                          {t(`playerModal.statuses.${s.value}.sub`)}
                        </span>
                      </button>
                    )
                  })}
                </div>
                {errors.status ? (
                  <p className={errorClass}>{errors.status}</p>
                ) : (
                  <p className="mt-1.5 text-xs text-zinc-500">
                    {t('playerModal.statusHelp')}
                  </p>
                )}
              </div>
            </div>

            {/* Primary Contact — coach captures one parent/guardian
                here for the team-side flow. Additional guardians (co-
                parent, grandparent, emergency contact) get added by
                the parent themselves through the registration form
                or self-service portal. The state still holds an array
                so multi-guardian records loaded from elsewhere (e.g.
                from the registration form once that supports it)
                don't get lost — only index 0 is editable here, and
                any additional entries are preserved on save. */}
            <div>
              <p className={sectionLabel} style={{ fontFamily: HEAD_FONT }}>
                {t('playerModal.sectionContact')}
              </p>

              <div className="rounded-xl border border-zinc-200 bg-[#f7faf5]/50 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.name')}</label>
                    <input
                      value={primaryGuardian.name}
                      onChange={(e) => updatePrimary({ name: e.target.value })}
                      className={inputClass}
                      placeholder={t('playerModal.phName')}
                    />
                  </div>
                  <div>
                    <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.relationship')}</label>
                    <select
                      value={primaryGuardian.relationship}
                      onChange={(e) => updatePrimary({ relationship: e.target.value })}
                      className={inputClass}
                    >
                      {RELATIONSHIPS.map((r) => (
                        <option key={r || 'unspecified'} value={r}>
                          {r ? t(`playerModal.relationships.${r}`) : t('playerModal.relSelect')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.email')}</label>
                  <input
                    type="email"
                    value={primaryGuardian.email}
                    onChange={(e) => updatePrimary({ email: e.target.value })}
                    className={inputClass}
                    placeholder={t('playerModal.phEmail')}
                  />
                </div>

                <div>
                  <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.phone')}</label>
                  <input
                    type="tel"
                    value={primaryGuardian.phone}
                    onChange={(e) => updatePrimary({ phone: e.target.value })}
                    className={inputClass}
                    placeholder={t('playerModal.phPhone')}
                  />
                </div>

                <div>
                  <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>
                    {t('playerModal.language')}
                  </label>
                  <div className="flex gap-2 mt-1">
                    {[
                      { value: 'en', label: '🇺🇸 English' },
                      { value: 'es', label: '🇲🇽 Español' },
                    ].map((opt) => {
                      const active = primaryGuardian.language === opt.value
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => updatePrimary({ language: opt.value })}
                          className={[
                            'flex-1 rounded-lg border-2 py-2 text-xs font-semibold transition-all inline-flex items-center justify-center gap-1.5',
                            active
                              ? 'border-[#b5f03d] bg-[#b5f03d]/10 text-[#0d1f14]'
                              : 'border-zinc-200 bg-white text-zinc-600 hover:border-[#b5f03d]/40',
                          ].join(' ')}
                        >
                          {active && <CheckIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />}
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              <p className="mt-2 text-xs text-zinc-500">
                {t('playerModal.contactHelp')}
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className={labelClass} style={{ fontFamily: HEAD_FONT }}>{t('playerModal.notes')}</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={onChange}
                rows={2}
                className={inputClass + ' resize-none'}
                placeholder={t('playerModal.phNotes')}
              />
            </div>
          </div>

          {/* Footer.
              Add mode shows three buttons: Cancel / Save & add another /
              Add Player. The "Save & add another" path commits the
              current entry then resets the form so the coach can
              rapid-fire a 15-kid roster without round-tripping through
              the modal each time. Edit mode hides "& add another"
              because there's nothing to add — they're editing one
              specific player. */}
          <div className="flex flex-wrap justify-end gap-2 px-6 py-4 border-t border-zinc-100 bg-[#f7faf5]">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2 text-sm font-bold uppercase text-[#0d1f14] hover:border-zinc-300 transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {t('playerModal.cancel')}
            </button>

            {!isEdit && (
              <button
                type="button"
                onClick={handleSubmitAndAddAnother}
                disabled={saving}
                className="inline-flex items-center justify-center rounded-xl border-2 border-[#b5f03d] bg-white text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase hover:bg-[#b5f03d]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {saving ? t('playerModal.saving') : t('playerModal.saveAddAnother')}
              </button>
            )}

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.97]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {saving ? t('playerModal.saving') : isEdit ? t('playerModal.saveChanges') : t('playerModal.addPlayer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
